import { useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type { AdjustStockPayload, InventoryBalance } from "../api/types";
import { isVesselItemCategory } from "../utils/vesselItemCategory";
import { Modal } from "./Modal";

export type AdjustInventoryStockModalProps = {
  row: InventoryBalance;
  onClose: () => void;
  onSaved: () => void;
};

function parseQty(s: string): number {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

export function AdjustInventoryStockModal({
  row,
  onClose,
  onSaved,
}: AdjustInventoryStockModalProps) {
  const current = parseQty(row.qtyOnHand);
  const [target, setTarget] = useState(String(current));
  const [diameterStr, setDiameterStr] = useState("");
  const [depthStr, setDepthStr] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showVessel = isVesselItemCategory(row.item.category.code);

  useEffect(() => {
    setTarget(String(parseQty(row.qtyOnHand)));
    setDiameterStr(row.item.diameterCm ?? "");
    setDepthStr(row.item.depthCm ?? "");
  }, [row]);

  const submit = async () => {
    const targetQty = parseFloat(target);
    if (!Number.isFinite(targetQty) || targetQty < 0) {
      setError("Enter a valid quantity (0 or greater).");
      return;
    }
    const r = reason.trim();
    if (r.length < 3) {
      setError("Reason must be at least 3 characters.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: AdjustStockPayload = {
        itemId: row.item.id,
        locationId: row.location.id,
        zoneId: row.zone.id,
        targetQtyOnHand: targetQty,
        reason: r,
      };
      if (showVessel) {
        const du = diameterStr.trim();
        if (du !== "") {
          const n = Number(du);
          if (!Number.isFinite(n) || n < 0 || n > 10_000) {
            setError("Diameter (cm) must be between 0 and 10000.");
            setSubmitting(false);
            return;
          }
          payload.targetDiameterCm = n;
        }
        const dp = depthStr.trim();
        if (dp !== "") {
          const n = Number(dp);
          if (!Number.isFinite(n) || n < 0 || n > 10_000) {
            setError("Depth (cm) must be between 0 and 10000.");
            setSubmitting(false);
            return;
          }
          payload.targetDepthCm = n;
        }
      }
      await api.adjustStock(payload);
      onSaved();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not update stock.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Adjust stock — ${row.item.name}`} onClose={onClose}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        {error ? (
          <div className="alert error" role="alert">
            {error}
          </div>
        ) : null}
        <p className="text-muted" style={{ margin: 0 }}>
          {row.zone.name} · {row.location.name}
        </p>
        <p style={{ margin: 0 }}>
          Current on hand:{" "}
          <strong>
            {row.qtyOnHand} {row.item.defaultUom.toLowerCase()}
          </strong>
        </p>
        {showVessel ? (
          <p style={{ margin: 0 }} className="text-muted">
            Catalog: diameter{" "}
            <strong>{row.item.diameterCm ?? "—"}</strong> cm · depth{" "}
            <strong>{row.item.depthCm ?? "—"}</strong> cm
          </p>
        ) : null}
        <div className="field">
          <label htmlFor="adj-qty">
            New quantity ({row.item.defaultUom.toLowerCase()})
          </label>
          <input
            id="adj-qty"
            type="number"
            min={0}
            step="any"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={submitting}
            autoComplete="off"
          />
        </div>
        {showVessel ? (
          <>
            <div className="field">
              <label htmlFor="adj-diameter">
                New diameter (cm) <span className="text-muted">(optional)</span>
              </label>
              <input
                id="adj-diameter"
                type="number"
                min={0}
                max={10_000}
                step="any"
                value={diameterStr}
                onChange={(e) => setDiameterStr(e.target.value)}
                disabled={submitting}
                placeholder="Leave blank to keep current"
              />
            </div>
            <div className="field">
              <label htmlFor="adj-depth">
                New depth (cm) <span className="text-muted">(optional)</span>
              </label>
              <input
                id="adj-depth"
                type="number"
                min={0}
                max={10_000}
                step="any"
                value={depthStr}
                onChange={(e) => setDepthStr(e.target.value)}
                disabled={submitting}
                placeholder="Leave blank to keep current"
              />
            </div>
          </>
        ) : null}
        <div className="field">
          <label htmlFor="adj-reason">Reason (audit trail)</label>
          <textarea
            id="adj-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            placeholder="e.g. Annual count correction"
          />
        </div>
        <div className="row-actions" style={{ marginTop: "var(--space-2)" }}>
          <button
            type="button"
            className="button"
            onClick={() => void submit()}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            className="button plain"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
